import type { Cue } from './model/cue.ts';
import type { Settings } from './settings/schema.ts';

/** Field separator — avoids JSON.stringify on the hot paint path. */
const SIG = '\x1f';

export type PaintSignatureInput = {
  active: Cue[];
  settings: Settings;
  hasOther: boolean;
  hidden: boolean;
  dualMode: boolean;
  delayMs: number;
  rawType: string | undefined;
  viewWidth: number;
  viewHeight: number;
};

/**
 * Cheap paint fingerprint (same visual inputs as the former JSON.stringify blob,
 * plus speakingMode which drives getRenderText under dictation).
 * Shared by extension RenderController and mobile SubtitleSyncEngine.
 */
export function buildPaintSignature(input: PaintSignatureInput): string {
  const { active, settings: s } = input;
  const parts: Array<string | number | boolean | undefined | null> = [];
  for (let i = 0; i < active.length; i += 1) {
    const cue = active[i]!;
    parts.push(
      cue.start,
      cue.end,
      cue.text,
      cue.translation,
      cue.translated,
      cue.loading,
      cue.dictation,
      cue.kuroshiro,
      cue.src,
    );
  }
  parts.push(
    s.subY,
    s.mainSubY,
    s.secondSubY,
    s.subtitleSeparation,
    s.spacing,
    s.maxWidth,
    s.mainFontSizeRatio,
    s.secondFontSizeRatio,
    s.mainFontColor,
    s.secondFontColor,
    s.mainFontWeight,
    s.secondFontWeight,
    s.mainStrokeWidth,
    s.secondStrokeWidth,
    s.mainStrokeColor,
    s.secondStrokeColor,
    s.mainShadow,
    s.secondShadow,
    s.mainShadowColor,
    s.secondShadowColor,
    s.mainFontBackgroundColor,
    s.secondFontBackgroundColor,
    s.mainFontBackgroundColorOpacity,
    s.secondFontBackgroundColorOpacity,
    s.mainFontFamily,
    s.secondFontFamily,
    s.mainFontGoogleFamilies,
    s.secondFontGoogleFamilies,
    s.mainFontGoogleCssProperties,
    s.secondFontGoogleCssProperties,
    s.mainLetterSpacing,
    s.secondLetterSpacing,
    s.mainLineHeight,
    s.secondLineHeight,
    s.mainPadding,
    s.secondPadding,
    s.mainOpacity,
    s.secondOpacity,
    s.mainBoxShadow,
    s.secondBoxShadow,
    input.hasOther,
    input.hidden,
    input.dualMode,
    input.delayMs,
    input.rawType,
    s.practiceMode,
    s.shadowingHideUpcoming,
    s.shadowingGrayPast,
    s.learningMode,
    s.dictationMode,
    s.dictationSubtitle,
    s.speakingMode,
    s.selectable,
    input.viewWidth,
    input.viewHeight,
  );
  return parts.join(SIG);
}
